import { Alert, Snackbar } from '@mui/material';
import React, { useState } from 'react';
import { Translate } from 'react-localize-redux';

import { SnackbarType, SnackbarTypeSeverity } from '../../../constants/types';
import AddServiceAction from './AddServiceAction';
import CancelRequestAction from './CancelRequestAction';
import ManualCompleteAction from './ManualCompleteAction';
import MaterialDeliveredAction from './MaterialDeliveredAction';
import RemoveServiceAction from './RemoveServiceAction';
import RequestManagementPanel from './RequestManagementPanel';
import SurgeryStatusAction from './SurgeryStatusAction';
import { canCancelRequest, canModifyRequestServices, RequestNextAction, requestNextAction, requestNextActionNoteId } from './requestModification';
import { IRequest, ServiceType } from './types';

interface RequestManagementProps {
    request: IRequest;
    requestServiceType: ServiceType;
    uuidInvestigation: string;
    permissions: string[];
    /** Called after a mutation the API confirmed; RequestSingle reloads the request list. */
    onChanged: () => void;
    /** Called after an item add / modify / delete the API confirmed; RequestSingle refreshes the
     *  request in place and stays on it. */
    onItemsChanged: () => void;
    setShowSnackbar: (snackbar: { show: boolean; message?: string; severity?: SnackbarTypeSeverity }) => void;
}

/**
 * SHER-1062 — the request-management panel filled for a real request.
 *
 * RequestManagementPanel is layout only; this is where the page decides what goes in each
 * slot, from the existing rules in requestModification.ts and nothing else. It is shared by
 * Laboratory, Radiology, Cordonnerie and Surgery, exactly as RequestSingle is.
 *
 * - Items are always listed: RemoveServiceAction renders read-only rows when the user may not
 *   modify, so a closed request still shows what was ordered.
 * - Add and cancel are offered only where the API would accept them.
 * - The next action is one of the existing workflow steps (requestNextAction), with the note
 *   saying what it records.
 * - Item changes stay on the request (Pedro, beta 2026-10-05: you want to see that the change
 *   landed). They go through onItemsChanged and confirm in this panel's own toast: the page's toast
 *   returns to the list when it closes, which is right for every other action but not for these.
 */
const RequestManagement: React.FC<RequestManagementProps> = ({ request, requestServiceType, uuidInvestigation, permissions, onChanged, onItemsChanged, setShowSnackbar }) => {
    const nextAction = requestNextAction(request, requestServiceType, permissions);
    const [itemSnackbar, setItemSnackbar] = useState<SnackbarType>({ show: false });

    const nextActionNode = (action: RequestNextAction | null) => {
        switch (action) {
            case 'deliver':
                return <MaterialDeliveredAction request={request} requestServiceType={requestServiceType} uuidInvestigation={uuidInvestigation}
                    permissions={permissions} onDelivered={onChanged} setShowSnackbar={setShowSnackbar} />;
            case 'complete':
                return <ManualCompleteAction request={request} requestServiceType={requestServiceType} uuidInvestigation={uuidInvestigation}
                    permissions={permissions} onCompleted={onChanged} setShowSnackbar={setShowSnackbar} />;
            case 'surgery':
                return <SurgeryStatusAction request={request} uuidInvestigation={uuidInvestigation} setShowSnackbar={setShowSnackbar} />;
            default:
                return null;
        }
    };

    return (
        <>
            <Snackbar anchorOrigin={{ vertical: 'top', horizontal: 'center' }} open={itemSnackbar.show} autoHideDuration={4000}
                onClose={() => setItemSnackbar({ show: false })}>
                <Alert severity={itemSnackbar.severity}>
                    <Translate id={itemSnackbar.message} />
                </Alert>
            </Snackbar>
            <RequestManagementPanel
                requestServiceType={requestServiceType}
                items={
                    <RemoveServiceAction request={request} uuidInvestigation={uuidInvestigation} permissions={permissions}
                        requestServiceType={requestServiceType} onChanged={onItemsChanged} setShowSnackbar={setItemSnackbar} />
                }
                addItem={!canModifyRequestServices(request, permissions) ? null :
                    <AddServiceAction request={request} uuidInvestigation={uuidInvestigation} requestServiceType={requestServiceType}
                        permissions={permissions} onChanged={onItemsChanged} setShowSnackbar={setItemSnackbar} />
                }
                currentStatus={request.status}
                nextAction={nextActionNode(nextAction)}
                nextActionNoteId={requestNextActionNoteId(request, nextAction)}
                cancel={!canCancelRequest(request, permissions) ? null :
                    <CancelRequestAction request={request} uuidInvestigation={uuidInvestigation} permissions={permissions}
                        onCancelled={onChanged} setShowSnackbar={setShowSnackbar} />
                }
            />
        </>
    );
};

export default RequestManagement;
